import { EntityType } from "@amzn/ask-legal-domain";
import { Alert, Box, ExpandableSection, SpaceBetween, TextContent } from "@amzn/awsui-components-react";
import * as React from "react";

/**
 * Explains the three-level permission scope hierarchy — Instance > Page Library > Page —
 * and how access cascades downward. Rendered at the top of the Permissions section in the
 * instance, page library, and page admin portals so admins understand where the permissions
 * they are editing sit in the hierarchy and what they cover.
 *
 * The `currentScope` (the entity type being edited) is highlighted so admins can orient
 * themselves within the hierarchy.
 */
export const PermissionHierarchyInfo = (props: { currentScope: EntityType }) => {
    const levels: {
        scope: EntityType;
        title: string;
        roles: string;
        covers: string;
    }[] = [
        {
            scope: EntityType.Instance,
            title: "Instance",
            roles: "Admin",
            covers: "The entire instance. Instance Admins can manage every Page Library and Page beneath it, "
                + "plus instance-wide settings, navigation, roles, and permissions. You grant a user access to "
                + "the instance by giving them a role on a Page Library or Page (see below) — that automatically "
                + "lets them enter the instance, open the home page, and use search. There is no separate "
                + "instance-viewer step."
        },
        {
            scope: EntityType.PageLibrary,
            title: "Page Library",
            roles: "Editor, Viewer",
            covers: "One Page Library and every Page inside it. A Page Library Editor can edit all pages and a "
                + "Viewer can view all pages in that library without being added to each page individually."
        },
        {
            scope: EntityType.Page,
            title: "Page",
            roles: "Editor, Viewer",
            covers: "A single Page only. Use page-level permissions to grant access to one page without "
                + "granting access to the rest of its Page Library."
        }
    ];

    return (
        <ExpandableSection
            variant="container"
            headerText="How Pathfinder permissions work"
            defaultExpanded={false}
        >
            <SpaceBetween direction="vertical" size="m">
                <TextContent>
                    <p>
                        Permissions are set at three levels. Access <b>cascades downward</b> — access granted
                        at a higher level automatically applies to everything beneath it, so you do not need to
                        re-grant it at each lower level.
                    </p>
                    <p>
                        <b>Instance &rarr; Page Library &rarr; Page</b>
                    </p>
                </TextContent>
                <Alert type="info">
                    <b>To give someone access, grant them a role on a Page Library or Page.</b> That
                    automatically lets them enter the instance — open the home page and use search — in addition
                    to seeing that content. There is no separate instance-viewer step: a user accessing an
                    instance they have been granted no Page Library or Page access to would see an access
                    denied page with an access request link. Be sure to set up access request links in the
                    instance admin 'Overview' tab.
                </Alert>
                <SpaceBetween direction="vertical" size="s">
                    {levels.map((level) => {
                        const isCurrent = level.scope === props.currentScope;
                        return (
                            <Box
                                key={level.scope}
                                padding="s"
                                variant="div"
                                color={isCurrent ? "text-status-info" : "inherit"}
                            >
                                <TextContent>
                                    <p>
                                        <b>{level.title}</b>
                                        {isCurrent && <em> &mdash; you are here</em>}
                                        <br />
                                        <small><b>Roles available:</b> {level.roles}</small>
                                        <br />
                                        <small>{level.covers}</small>
                                    </p>
                                </TextContent>
                            </Box>
                        );
                    })}
                </SpaceBetween>
                <TextContent>
                    <p>
                        <small>
                            <b>Roles:</b> <b>Admin</b> has full control including managing permissions and settings.
                            {" "}<b>Approver</b> reviews and publishes drafts submitted by Editors.
                            {" "}<b>Editor</b> creates and edits content. <b>Viewer</b> has read-only access.
                        </small>
                    </p>
                </TextContent>
            </SpaceBetween>
        </ExpandableSection>
    );
};
